/**
 * The shared records that hang off a SchoolClass, and the calls that edit them.
 *
 * These editors are used by BOTH the student app (Settings → Class & Study
 * Plan) and the admin console (a class's detail page). That is possible because
 * both hit the *same* endpoints — an admin passes the membership gate without
 * being a member, since these are shared records in the public tenant and the
 * platform is the authority of last resort where no student or teacher owns
 * them.
 *
 * The API is injected rather than imported. Each app already has its own fetch
 * wrapper — different base URL handling, different auth header, different error
 * shape — and threading those through props keeps this component free of both.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/StudyPlanning/domain-model.md §3.2–3.3.
 */

export type ClassExamType = 'unit-test' | 'class-test' | 'pre-board' | 'board' | 'mock';

/** One sitting: the thing a deadline actually counts back from. */
export interface ExamPaper {
  id: string;
  /** PLURAL — a CBSE paper is one subject, a JEE paper is three. */
  subjects: string[];
  label?: string;
  /** ISO 'YYYY-MM-DD'. */
  date: string;
  time?: string;
  durationMinutes?: number;
  /** ABSENT means the whole curriculum — not the same as an empty array. */
  scope?: string[];
  curriculumId?: string;
  maxMarks?: number;
}

/** Which level an exam was recorded at. */
export type ClassExamOwnerScope =
  'schoolClassSection' | 'schoolClass' | 'school' | 'board' | 'platform';

/** A name over one or more papers: the boards are twelve, a unit test is one. */
export interface ClassExam {
  id: string;
  ownerId: string;
  ownerScope?: ClassExamOwnerScope;
  name: string;
  type: ClassExamType;
  papers: ExamPaper[];
  createdAt?: string;
  /** Last edit — falls back to creation server-side, so a never-edited exam
   *  still says who and when. */
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

/**
 * WHOSE exams — the whole grade's, or one section's.
 *
 * Exams are recorded at the level that sits them and read downward: the boards belong to
 * the class and every section inherits them, while a Friday unit test belongs to the one
 * section sitting it. Passing an owner rather than a bare class id is what keeps the two
 * apart at every call site, so nothing can write a section's test onto the whole grade.
 *
 * `sectionId` absent = the class itself.
 * See docs/FeatureDesigns/school-teachers-parents/schoolclass-model-target.md §4.4.
 */
export interface ExamOwner {
  schoolClassId: string;
  sectionId?: string;
}

/** One section of a class, as much of it as any picker needs. */
export interface ClassSection {
  id: string;
  name: string;
}

export interface ExamInput {
  name: string;
  type: ClassExamType;
  papers: Array<Omit<ExamPaper, 'id'> & { id?: string }>;
}

/** One member's statement about one chapter. */
export interface ChapterCoverage {
  chapterId: string;
  /** Members currently saying it is covered — the corroboration count. */
  coveredBy: string[];
  /** Members currently saying it is not. Both non-empty = the class disagrees. */
  disputedBy: string[];
  lastReportedAt?: string;
  /** More say covered than not. A tie is deliberately NOT covered. */
  covered: boolean;
}

/** One pass in a plan — the AUTHOR's own names, copied on adopt and never synced
 *  afterwards, so renaming a pass in a plan does not rewrite what a student who
 *  already took it calls it. */
export interface StudyPlanPass {
  id: string;
  name: string;
  order: number;
}

export type StudyPlanOwnerKind = 'schoolClass' | 'user' | 'platform';

/**
 * One subject inside a plan, carrying the two things that cannot be shared
 * across subjects: which syllabus it follows, and when each chapter lands.
 *
 * `curriculumId` ABSENT means this subject says nothing about chapters and so
 * transfers to any syllabus for it. Binding is per subject rather than per plan,
 * so one pinned subject cannot poison the rest.
 */
export interface StudyPlanSubject {
  subject: string;
  curriculumId?: string;
  /** passId → per-chapter dates. The finer grain: a pass date says "done by
   *  March", these say when each chapter lands. */
  chapterTargets?: Record<string, Array<{ chapterId: string; targetDate: string }>>;
}

/** Who may adopt a plan. `private` is a real state, not an absence: it is what
 *  makes publishing a deliberate act rather than a side effect of writing. */
export type StudyPlanVisibility = 'private' | 'class' | 'listed';

/** The exam a plan was planned towards. What makes its dates judgeable — a plan
 *  aimed at the March boards means something different in July than in November. */
export interface StudyPlanWrittenFor {
  label?: string;
  /** ISO 'YYYY-MM-DD'. */
  examDate?: string;
}

/**
 * An authored plan — a class's, a student's, or the platform's.
 *
 * Was `ClassPlan`, whose identity WAS `(schoolClassId, subject)`, so a class could
 * hold exactly one and a plan belonging to no class could not exist. It now has an
 * opaque `id` and a `name`, which is what lets a class offer "Steady" and
 * "Catch-up from October" side by side.
 *
 * Never a student's own plan. A student's dates say "when I intend to finish";
 * this says "when the author intends to", and adopting COPIES one into the other.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/StudyPlanning/shared-plans.md.
 */
/**
 * What a take actually did.
 *
 * A plan covers a set of subjects and adoption writes one document per subject,
 * without a transaction — so the honest answer is per subject rather than a
 * single success. `notTaken` carries the ones that did not land and why.
 */
export interface AdoptOutcome {
  subjects: Array<{ subject: string; applied: string[]; skipped: string[] }>;
  notTaken: Array<{ subject: string; reason: string }>;
}

export interface StudyPlan {
  id: string;
  ownerId: string;
  ownerKind: StudyPlanOwnerKind;
  visibility: StudyPlanVisibility;
  /** The author's own words — what a student picks between. NOT the identity:
   *  two plans may both be called "Steady". */
  name: string;
  description?: string;
  /** The subjects this plan covers. A plan is a SET of subjects on ONE timeline:
   *  "ICSE 10 — Steady" is three sweeps across physics, maths and the rest. */
  subjects: StudyPlanSubject[];
  passes: StudyPlanPass[];
  /** passId → ISO 'YYYY-MM-DD'. ONE timeline, shared by every subject in the plan.
   *  A student who wants hindi earlier moves it on THEIR copy after adopting. */
  passDates: Record<string, string>;
  writtenFor?: StudyPlanWrittenFor;
  /**
   * The level this plan targets. A curriculum is a (board, grade, subject)
   * triple, so a plan holding the first two resolves the third for any subject
   * it takes on — which is what lets a subject be added without its caller
   * looking up a syllabus. Absent on plans authored before this.
   */
  board?: string;
  grade?: string;
  /** How many students took it. Honest about being a weak signal: it measures
   *  what was taken, never what worked. */
  adoptions?: number;
  /** THE plan for this (board, grade) — what a student who has chosen nothing is
   *  started on. At most one plan per pair carries it, and only a plan from the
   *  platform ever can. */
  isDefault?: boolean;
  version: number;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  /** Resolved server-side: the document holds user ids, and a list showing ids
   *  tells the reader nothing while resolving them per row would be one request
   *  each. Absent for a user who no longer exists. */
  createdByName?: string;
  updatedByName?: string;
}

/**
 * The plan calls alone.
 *
 * Split out of `ClassSharedApi` because the plan surfaces are reachable by a
 * student in NO class — most students — and demanding exam and coverage methods
 * from a caller that only writes plans would force it to supply stubs.
 */
export interface StudyPlanApi {
  /** Plans one owner holds — several per owner now, so this is a list and not a
   *  slot. Includes `private` drafts, which only the owner can reach. */
  listPlans(q: {
    ownerKind: StudyPlanOwnerKind; ownerId?: string;
  }): Promise<{ plans: StudyPlan[] }>;
  /** A new, named, EMPTY plan. Starts `private` whatever the owner: a plan with
   *  no dates in it is of no use to anyone, and making the first save the moment
   *  it becomes visible would put every half-written plan in front of the class. */
  createPlan(body: {
    ownerKind: StudyPlanOwnerKind; ownerId?: string; name: string;
    subjects?: Array<{ subject: string; curriculumId?: string }>;
    description?: string;
    passes?: StudyPlanPass[]; writtenFor?: StudyPlanWrittenFor;
    /** Stamped at creation; a subject added later needs no syllabus id. */
    board?: string; grade?: string;
  }): Promise<{ plan: StudyPlan }>;
  /** One cell per call, deliberately: the history entry is then exactly what
   *  changed, which is what every adopter's refresh prompt renders. A bulk
   *  write collapses into one unreadable "the plan changed". */
  editPlan(
    planId: string,
    body: {
      ownerId: string;
      passId: string;
      /** WHICH subject's chapter. Required with `chapterId`, and meaningless
       *  without it — a pass date is the plan's one shared timeline. */
      subject?: string;
      /** Present = a per-chapter date; absent = the whole pass. */
      chapterId?: string;
      /** A date sets it, `null` clears it, and ABSENT means this write is not
       *  about a date at all — renaming a pass must not wipe the date it has. */
      date?: string | null;
      passes?: StudyPlanPass[];
    },
  ): Promise<{ plan: StudyPlan }>;
  /** Name, description, who it was written for. Separate from `editPlan` because
   *  it must NOT bump `version` or write history: a rename is not a change an
   *  adopter should be prompted about and then shown nothing for. */
  editPlanDetails(
    planId: string,
    body: {
      ownerId: string; name?: string; description?: string; writtenFor?: StudyPlanWrittenFor;
      /** Only while the plan has no subjects — their chapter dates belong to the
       *  syllabus they were added from. */
      board?: string; grade?: string;
      /** Make this the default for its level, or stop it being that. Admin-only
       *  server-side, and it un-defaults whichever plan held the level before. */
      isDefault?: boolean;
    },
  ): Promise<{ plan: StudyPlan }>;
  /** Add or remove a subject. Separate from `editPlan` because it changes what the
   *  plan COVERS rather than when something is due. */
  editPlanSubjects(
    planId: string,
    body: { ownerId: string; subject: string; curriculumId?: string; include?: boolean },
  ): Promise<{ plan: StudyPlan }>;
  deletePlan(planId: string, ownerId: string): Promise<unknown>;
  /** Publish, unpublish, or (as an admin) unlist. One call for all three because
   *  they are one act with different arguments. */
  setPlanVisibility(
    planId: string, body: { ownerId: string; visibility: StudyPlanVisibility },
  ): Promise<unknown>;
}

/**
 * What an app must supply for the whole class surface. Exam methods take an
 * ExamOwner rather than a bare class id — a section's test and the grade's
 * board timetable are different owners, and one id could not tell them apart.
 */
export interface ClassSharedApi extends StudyPlanApi {
  /** The owner's OWN exams — never the union. An authoring surface has to show what is
   *  editable here apart from what is inherited, or someone edits the board timetable
   *  believing they are fixing their own section's date. */
  listExams(owner: ExamOwner): Promise<{ exams: ClassExam[] }>;
  createExam(owner: ExamOwner, body: ExamInput): Promise<{ exam: ClassExam }>;
  updateExam(owner: ExamOwner, examId: string, body: Partial<ExamInput>): Promise<{ exam: ClassExam }>;
  deleteExam(owner: ExamOwner, examId: string): Promise<unknown>;
  /** Correct the LEVEL of an exam already recorded. `toSectionId: null` is the class —
   *  what the whole grade sits. Server-side rather than a delete-and-recreate from here,
   *  so the exam keeps the id every deadline names and both ends are gated together. */
  moveExam(owner: ExamOwner, examId: string, toSectionId: string | null): Promise<{ exam: ClassExam }>;
  /** `names` maps every reporter's userId to their display name — resolved
   *  server-side, since a reporter need not be a member of the class. */
  getCoverage(schoolClassId: string, subject: string): Promise<{
    chapters: ChapterCoverage[];
    names?: Record<string, string>;
  }>;
  reportCoverage(
    schoolClassId: string,
    body: { subject: string; chapterId: string; curriculumId: string; covered: boolean },
  ): Promise<unknown>;
}

export const EXAM_TYPE_LABELS: Record<ClassExamType, string> = {
  'unit-test': 'Unit test',
  'class-test': 'Class test',
  'pre-board': 'Pre-board',
  board: 'Board',
  mock: 'Mock',
};
